import React, { useEffect, useMemo } from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { app } from "@microsoft/teams-js";
import { ApiClient } from "@dining/api-client";
import {
  BuildingListView,
  CafeView,
  cafeViewLoader,
  Checkout,
  MenuItemDetails,
  menuItemDetailsLoader,
  MenuList,
  menuListLoader,
  OrderHistoryListView,
  OrderSummaryCard,
} from "./components";
import { Root, LoadingScreen, NavigationHeader } from "./components/shared";
import { AppContextType, AppType } from "@microsoft/metaos-appcontextutils";
import { AriaLiveAnnouncer, FluentProvider, makeStyles, tokens } from "@fluentui/react-components";
import {
  appInsightsEvents,
  Client,
  initAppContext,
  initializeMsal,
  lightTheme,
  trackEvent,
  useAppStore,
  darkTheme,
  useThemeDetector,
} from "./common";
import { OrderDetailsCard } from "./components/OrderView/OrderDetails/OrderDetailsCard";
import { ErrorBoundaryView } from "./components/shared/ErrorBoundary";
import { ErrorBoundary } from "react-error-boundary";
import InactivityHandler from "./components/shared/InactivityHandler";

const useStyles = makeStyles({
  app: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "stretch",
    alignItems: "stretch",
    minHeight: "100vh",
    width: "100vw",
    "@media (min-width: 500px)": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    backgroundColor: tokens.colorNeutralBackground3,
  },
});

const App = () => {
  const styles = useStyles();
  const setContext = useAppStore((state) => state.setContext);
  const updateContext = useAppStore((state) => state.updateContext);
  const client = useAppStore((state) => state.client);
  const setClient = useAppStore((state) => state.setClient);
  const fetchDiningAgent = useAppStore((state) => state.featureFlags.fetchDiningAgent);
  const fetchVoiceInput = useAppStore((state) => state.featureFlags.fetchVoiceInput);
  const authenticated = useAppStore((state) => state.authenticated);
  const setAuthenticated = useAppStore((state) => state.setAuthenticated);
  const selectedDate = useAppStore((state) => state.selectedDate);
  const appInsights = useAppStore((state) => state.appInsights);
  const isDarkTheme = useThemeDetector();

  useEffect(() => {
    const initializeApp = async () => {
      let context: AppContextType = { appType: AppType.standAlone };
      let clientInstance: ApiClient | undefined = undefined;

      app
        .initialize()
        .then(() => {
          context.appType = AppType.placesMetaOs;
        })
        .catch(() => {})
        .finally(async () => {
          try {
            await initializeMsal();
            context = await initAppContext(context.appType);
            context && setContext(context);
            setAuthenticated(true);
            clientInstance = new Client(
              appInsights,
              updateContext,
              context.appType === AppType.standAlone ? "webapp" : "Places",
            ).instance;
            clientInstance && setClient(clientInstance);
            if (clientInstance) {
              fetchDiningAgent();
              fetchVoiceInput();
            }
          } catch (error) {
            setAuthenticated(false);
            appInsights.trackException({ exception: error as Error }, { method: "initializeApp" });
          }
          trackEvent(appInsights, appInsightsEvents.System.AppInitialized, context);
        });
    };

    initializeApp();
  }, [appInsights]);

  /* ? Memoized router (critical fix) */
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: "/",
          element: <Root />,
          children: [
            { path: "/error", element: <ErrorBoundaryView /> },
            {
              path: "/buildings",
              element: (
                <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
                  <BuildingListView />
                </ErrorBoundary>
              ),
            },
            {
              path: "/cafe/:cafeId",
              element: (
                <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
                  <NavigationHeader />
                  <CafeView />
                </ErrorBoundary>
              ),
              loader: client ? cafeViewLoader(appInsights, client, selectedDate) : undefined,
            },
            {
              element: (
                <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
                  <NavigationHeader />
                  <Outlet />
                </ErrorBoundary>
              ),
              children: [
                {
                  path: "/cafe/:cafeId/station/:stationId",
                  element: (
                    <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
                      <MenuList />
                    </ErrorBoundary>
                  ),
                  loader: client ? menuListLoader(appInsights, client, selectedDate) : undefined,
                },
                {
                  path: "/cafe/:cafeId/station/:stationId/item/:menuItemId",
                  element: (
                    <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
                      <MenuItemDetails />
                    </ErrorBoundary>
                  ),
                  loader: client ? menuItemDetailsLoader(client, selectedDate) : undefined,
                },
                {
                  path: "/cafe/:cafeId/checkout",
                  element: (
                    <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
                      <Checkout />
                    </ErrorBoundary>
                  ),
                },
                {
                  path: "/cafe/:cafeId/station/:stationId/item/:menuItemId/edit/:editItemId",
                  element: (
                    <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
                      <MenuItemDetails />
                    </ErrorBoundary>
                  ),
                  loader: client ? menuItemDetailsLoader(client, selectedDate) : undefined,
                },
                {
                  path: "/cafe/:cafeId/order/:orderId",
                  element: (
                    <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
                      <OrderDetailsCard />
                    </ErrorBoundary>
                  ),
                },
                {
                  path: "/orders/past",
                  element: (
                    <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
                      <OrderHistoryListView />
                    </ErrorBoundary>
                  ),
                },
                {
                  path: "/orders/past/:orderId",
                  element: (
                    <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
                      <OrderSummaryCard />
                    </ErrorBoundary>
                  ),
                },
              ],
            },
          ],
        },
      ]),
    [appInsights, client, selectedDate], // dependencies
  );

  return (
    <FluentProvider theme={isDarkTheme ? darkTheme : lightTheme}>
      <div className={styles.app}>
        <InactivityHandler />
        <main role="main" aria-label="Main Content">
          {authenticated && client && !!client.healthCheckPing ? (
            <ErrorBoundary FallbackComponent={ErrorBoundaryView}>
              <AriaLiveAnnouncer>
                <RouterProvider router={router} />
              </AriaLiveAnnouncer>
            </ErrorBoundary>
          ) : (
            <LoadingScreen />
          )}
        </main>
      </div>
    </FluentProvider>
  );
};

export default App;
